// File Actions Component (Edit, Delete, etc.)
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Edit2, Trash2, Download, MoreVertical, FolderInput, ListChecks, Tag, Loader2, Youtube, GitBranch, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { AudioFile, FileStatus } from '@types';
import { useFileStore } from '@stores/useFileStore';
import { useToast } from '@components/ui';
import { cn } from '@utils/classNames';
import { LinkFilesModal } from './LinkFilesModal';
import { EditSellingInfoModal } from './EditSellingInfoModal';
import { QuickEditModal } from '@features/cloud-explorer';
import { AddToBeatPackModal } from '@features/sharing/components/AddToBeatPackModal';
import { SUSPENSION_ERROR_MESSAGE, showSuspensionToastIfNeeded } from '@utils/suspension';
import { ROUTES } from '@config/constants';

type ActivePanel = 'main' | 'status' | 'move';

interface FileActionsProps {
  file: AudioFile;
  onRename?: () => void;
  onDelete?: () => void;
  onMove?: () => void;
  className?: string;
}

export const FileActions: React.FC<FileActionsProps> = ({
  file,
  onRename,
  onDelete,
  onMove,
  className,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [activePanel, setActivePanel] = useState<ActivePanel>('main');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showSellingModal, setShowSellingModal] = useState(false);
  const [showEditTagsModal, setShowEditTagsModal] = useState(false);
  const [showAddToBeatPackModal, setShowAddToBeatPackModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { updateFile, deleteFile, getBlobUrl, folders, moveFile, files } = useFileStore();
  const { success, error: showError } = useToast();

  const closeMenu = () => {
    setShowMenu(false);
    setActivePanel('main');
  };

  useEffect(() => {
    if (!showMenu) return;

    const updatePosition = () => {
      const trigger = buttonRef.current;
      const menu = menuRef.current;
      if (!trigger || !menu) return;

      const rect = trigger.getBoundingClientRect();
      const menuHeight = menu.offsetHeight || 0;
      const menuWidth = menu.offsetWidth || 0;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const margin = 12;

      const miniPlayer = document.querySelector('[data-miniplayer]') as HTMLElement | null;
      const miniPlayerHeight = miniPlayer ? miniPlayer.offsetHeight : 0;
      const bottomMargin = margin + miniPlayerHeight;

      let top = rect.bottom + 8;
      if (top + menuHeight > viewportHeight - bottomMargin) {
        top = rect.top - menuHeight - 8;
        if (top < margin) {
          top = Math.max(margin, viewportHeight - menuHeight - bottomMargin);
        }
      }

      let left = rect.right - menuWidth;
      if (left < margin) {
        left = margin;
      }
      if (left + menuWidth > viewportWidth - margin) {
        left = Math.max(margin, viewportWidth - menuWidth - margin);
      }

      setMenuPosition({ top, left });
    };

    const raf = requestAnimationFrame(updatePosition);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [showMenu, activePanel]);

  useEffect(() => {
    if (!showMenu) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (activePanel !== 'main') {
          setActivePanel('main');
        } else {
          closeMenu();
        }
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        closeMenu();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [showMenu, activePanel]);

  const handleDownload = async () => {
    const url = await getBlobUrl(file.id);
    if (url) {
      const a = document.createElement('a');
      a.href = url;
      a.download = file.originalName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
    closeMenu();
  };

  const groupFiles = file.groupId
    ? files.filter((f) => f.groupId === file.groupId)
    : [];

  const handleDeleteGroup = async () => {
    setIsDeleting(true);
    try {
      await Promise.all(groupFiles.map((f) => deleteFile(f.id)));
      success('Group deleted', `${groupFiles.length} file${groupFiles.length !== 1 ? 's' : ''} have been deleted.`);
      onDelete?.();
    } catch (err) {
      showError('Failed to delete group', 'Please try again.');
    } finally {
      setIsDeleting(false);
      setShowMenu(false);
      setActivePanel('main');
      setShowDeleteConfirm(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteFile(file.id);
      success('File deleted', `"${file.name}" has been deleted successfully.`);
      onDelete?.();
    } catch (err) {
      showError('Failed to delete file', 'Please try again.');
    } finally {
      setIsDeleting(false);
      setShowMenu(false);
      setActivePanel('main');
      setShowDeleteConfirm(false);
    }
  };

  const handleRename = () => {
    onRename?.();
    closeMenu();
  };

  const handleMoveToFolder = async (folderId: string | null) => {
    try {
      await moveFile(file.id, folderId === null ? null : folderId);
      success('File moved', `"${file.name}" has been moved successfully.`);
      onMove?.();
    } catch (err) {
      showError('Failed to move file', 'Please try again.');
    }
    closeMenu();
  };

  const handleStatusChange = async (status: FileStatus) => {
    try {
      await updateFile(file.id, { status });
      success('Status updated', `File status changed to "${status}"`);
      onMove?.();
    } catch (err: any) {
      if (err.message === 'ACCOUNT_SUSPENDED') {
        showError(SUSPENSION_ERROR_MESSAGE.title, SUSPENSION_ERROR_MESSAGE.message);
      } else {
        showError('Failed to update status', 'Please try again.');
      }
    }
    closeMenu();
  };

  const statusOptions = [
    { value: FileStatus.WIP, label: 'WIP', emoji: '🚧' },
    { value: FileStatus.DRAFT, label: 'Draft', emoji: '🟡' },
    { value: FileStatus.TAGGED, label: 'Tagged', emoji: '🏷️' },
    { value: FileStatus.UNTAGGED, label: 'Clean', emoji: '✨' },
    { value: FileStatus.READY, label: 'Ready', emoji: '🟢' },
    { value: FileStatus.SOLD, label: 'Sold', emoji: '✅' },
    { value: FileStatus.ARCHIVED, label: 'Archived', emoji: '⚫' },
  ];

  const subPanelHeader = (label: string) => (
    <button
      onClick={() => setActivePanel('main')}
      className="w-full flex items-center gap-2 px-3 py-2.5 text-white/60 hover:text-white hover:bg-white/5 transition-colors text-left border-b border-white/5"
    >
      <ChevronLeft className="w-3.5 h-3.5 flex-shrink-0" />
      <span className="font-inter text-xs font-medium uppercase tracking-wide">{label}</span>
    </button>
  );

  return (
    <>
      <div className={cn('relative', className)} data-file-actions>
        <button
          ref={buttonRef}
          onClick={(e) => {
            e.stopPropagation();
            if (showMenu) {
              closeMenu();
            } else {
              setShowMenu(true);
            }
          }}
          className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-all"
        >
          <MoreVertical className="w-5 h-5" />
        </button>
      </div>

      {showMenu &&
        createPortal(
          <div
            className="fixed w-56 bg-dark-secondary/95 backdrop-blur-md border border-white/10 rounded-xl shadow-2xl z-[130] overflow-hidden"
            style={{
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
            }}
            ref={menuRef}
          >
            {/* ── MAIN PANEL ── */}
            {activePanel === 'main' && (
              <div className="py-1">
                {/* Group: Edit */}
                <button
                  onClick={handleRename}
                  className="w-full flex items-center gap-3 px-3.5 py-2.5 text-white hover:bg-white/10 transition-colors text-left"
                >
                  <Edit2 className="w-4 h-4 text-white/60 flex-shrink-0" />
                  <span className="font-inter text-sm">Rename</span>
                </button>

                <button
                  onClick={() => {
                    closeMenu();
                    setShowEditTagsModal(true);
                  }}
                  className="w-full flex items-center gap-3 px-3.5 py-2.5 text-white hover:bg-white/10 transition-colors text-left"
                >
                  <Tag className="w-4 h-4 text-white/60 flex-shrink-0" />
                  <span className="font-inter text-sm">Edit Tags</span>
                </button>

                <button
                  onClick={() => setActivePanel('status')}
                  className="w-full flex items-center justify-between gap-3 px-3.5 py-2.5 text-white hover:bg-white/10 transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    <ListChecks className="w-4 h-4 text-white/60 flex-shrink-0" />
                    <span className="font-inter text-sm">Change Status</span>
                  </div>
                  <ChevronRight className="w-3.5 h-3.5 text-white/40 flex-shrink-0" />
                </button>

                <div className="h-px bg-white/5 my-1" />

                {/* Group: Versions & Linking */}
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    try {
                      const newValue = !file.isMainVersion;
                      await updateFile(file.id, { isMainVersion: newValue });
                      success(newValue ? 'Set as main version' : 'Auto-detection enabled');
                      closeMenu();
                      onMove?.();
                    } catch (err) {
                      showError('Failed to update main version');
                    }
                  }}
                  className="w-full flex items-center gap-3 px-3.5 py-2.5 text-white hover:bg-white/10 transition-colors text-left"
                >
                  <span className="text-base w-4 flex-shrink-0 text-center">{file.isMainVersion ? '📌' : '📍'}</span>
                  <div>
                    <div className="font-inter text-sm">
                      {file.isMainVersion ? 'Unset Main Version' : 'Set as Main Version'}
                    </div>
                    <div className="text-xs text-white/40">
                      {file.isMainVersion ? 'Use automatic detection' : 'Always show this as main'}
                    </div>
                  </div>
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    closeMenu();
                    navigate(`${ROUTES.VERSION}?group=${encodeURIComponent(file.groupId || file.id)}`);
                  }}
                  className="w-full flex items-center gap-3 px-3.5 py-2.5 text-white hover:bg-white/10 transition-colors text-left"
                >
                  <GitBranch className="w-4 h-4 text-white/60 flex-shrink-0" />
                  <div>
                    <div className="font-inter text-sm">View Versions</div>
                    <div className="text-xs text-white/40">See all versions of this track</div>
                  </div>
                </button>

                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    const isSuspended = await showSuspensionToastIfNeeded(showError);
                    if (isSuspended) {
                      closeMenu();
                      return;
                    }
                    closeMenu();
                    setShowLinkModal(true);
                  }}
                  className="w-full flex items-center gap-3 px-3.5 py-2.5 text-white hover:bg-white/10 transition-colors text-left"
                >
                  <span className="text-base w-4 flex-shrink-0 text-center">🔗</span>
                  <div>
                    <div className="font-inter text-sm">Link to Files...</div>
                    <div className="text-xs text-white/40">Manually group with other files</div>
                  </div>
                </button>

                {file.groupId && (
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        await updateFile(file.id, { groupId: undefined });
                        success('File unlinked', 'File is now standalone');
                        closeMenu();
                        onMove?.();
                      } catch (err) {
                        showError('Failed to unlink file');
                      }
                    }}
                    className="w-full flex items-center gap-3 px-3.5 py-2.5 text-white hover:bg-white/10 transition-colors text-left"
                  >
                    <span className="text-base w-4 flex-shrink-0 text-center">🔓</span>
                    <div>
                      <div className="font-inter text-sm">Unlink from Group</div>
                      <div className="text-xs text-white/40">Make this file standalone</div>
                    </div>
                  </button>
                )}

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    closeMenu();
                    setShowAddToBeatPackModal(true);
                  }}
                  className="w-full flex items-center gap-3 px-3.5 py-2.5 text-white hover:bg-white/10 transition-colors text-left"
                >
                  <span className="text-base w-4 flex-shrink-0 text-center">📦</span>
                  <div>
                    <div className="font-inter text-sm">Add to Beat Pack</div>
                    <div className="text-xs text-white/40">Add this track to existing or new pack</div>
                  </div>
                </button>

                <button
                  onClick={() => setActivePanel('move')}
                  className="w-full flex items-center justify-between gap-3 px-3.5 py-2.5 text-white hover:bg-white/10 transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    <FolderInput className="w-4 h-4 text-white/60 flex-shrink-0" />
                    <span className="font-inter text-sm">Move to...</span>
                  </div>
                  <ChevronRight className="w-3.5 h-3.5 text-white/40 flex-shrink-0" />
                </button>

                <div className="h-px bg-white/5 my-1" />

                {/* Group: Export */}
                <button
                  onClick={handleDownload}
                  className="w-full flex items-center gap-3 px-3.5 py-2.5 text-white hover:bg-white/10 transition-colors text-left"
                >
                  <Download className="w-4 h-4 text-white/60 flex-shrink-0" />
                  <span className="font-inter text-sm">Download</span>
                </button>

                <button
                  onClick={() => {
                    closeMenu();
                    setShowSellingModal(true);
                  }}
                  className="w-full flex items-center gap-3 px-3.5 py-2.5 text-white hover:bg-white/10 transition-colors text-left"
                >
                  <ExternalLink className="w-4 h-4 text-white/60 flex-shrink-0" />
                  <div>
                    <div className="font-inter text-sm">BeatStars &amp; collaborator</div>
                    <div className="text-xs text-white/40">Sell link, price, share page</div>
                  </div>
                </button>

                <button
                  onClick={() => {
                    closeMenu();
                    navigate(`${ROUTES.YOUTUBE}?fileId=${encodeURIComponent(file.id)}`);
                  }}
                  className="w-full flex items-center gap-3 px-3.5 py-2.5 text-white hover:bg-white/10 transition-colors text-left"
                >
                  <Youtube className="w-4 h-4 text-white/60 flex-shrink-0" />
                  <span className="font-inter text-sm">Share to YouTube</span>
                </button>

                <div className="h-px bg-white/5 my-1" />

                {/* Group: Danger */}
                <button
                  onClick={() => {
                    closeMenu();
                    setShowDeleteConfirm(true);
                  }}
                  className="w-full flex items-center gap-3 px-3.5 py-2.5 text-danger hover:bg-danger/10 transition-colors text-left"
                >
                  <Trash2 className="w-4 h-4 flex-shrink-0" />
                  <span className="font-inter text-sm">Delete</span>
                </button>
              </div>
            )}

            {/* ── STATUS PANEL ── */}
            {activePanel === 'status' && (
              <div>
                {subPanelHeader('Change Status')}
                <div className="py-1 max-h-64 overflow-y-auto">
                  {statusOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleStatusChange(option.value)}
                      className={cn(
                        'w-full flex items-center gap-3 px-3.5 py-2.5 transition-colors text-left text-sm',
                        file.status === option.value
                          ? 'bg-primary/20 text-primary'
                          : 'text-white hover:bg-white/10'
                      )}
                    >
                      <span className="text-base w-4 flex-shrink-0 text-center">{option.emoji}</span>
                      <span className="font-inter">{option.label}</span>
                      {file.status === option.value && (
                        <span className="ml-auto text-xs opacity-70">✓</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ── MOVE PANEL ── */}
            {activePanel === 'move' && (
              <div>
                {subPanelHeader('Move to')}
                <div className="py-1 max-h-64 overflow-y-auto">
                  <button
                    onClick={() => handleMoveToFolder(null)}
                    className="w-full flex items-center gap-3 px-3.5 py-2.5 text-white hover:bg-white/10 transition-colors text-left text-sm font-inter"
                  >
                    <span className="text-base w-4 flex-shrink-0 text-center">📁</span>
                    <span>Root</span>
                  </button>
                  {folders.map((folder) => (
                    <button
                      key={folder.id}
                      onClick={() => handleMoveToFolder(folder.id)}
                      className="w-full flex items-center gap-3 px-3.5 py-2.5 text-white hover:bg-white/10 transition-colors text-left text-sm font-inter"
                    >
                      <span className="text-base w-4 flex-shrink-0 text-center">📁</span>
                      <span className="truncate">{folder.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>,
          document.body
        )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm &&
        createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="bg-dark-secondary/95 backdrop-blur-md border border-white/10 rounded-xl shadow-2xl p-4 max-w-md w-full animate-fade-in">
              <h3 className="font-dela text-base text-white mb-2">Delete File?</h3>
              <p className="font-inter text-sm text-white/70 mb-4">
                Are you sure you want to delete <span className="text-primary font-bold truncate block">{file.name}</span>?
                {groupFiles.length > 1 && (
                  <span className="block mt-1 text-white/50">
                    This file has {groupFiles.length - 1} other version{groupFiles.length - 1 !== 1 ? 's' : ''} in its group. You can delete only this file or the entire group.
                  </span>
                )}
                {groupFiles.length <= 1 && (
                  <span className="block mt-1">This action cannot be undone.</span>
                )}
              </p>
              <div className="flex gap-2 justify-end flex-wrap">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-3 py-2 text-sm bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all font-inter font-bold"
                >
                  Cancel
                </button>
                {groupFiles.length > 1 && (
                  <button
                    onClick={handleDeleteGroup}
                    disabled={isDeleting}
                    className="px-3 py-2 text-sm bg-gradient-to-r from-red-700 to-red-800 hover:from-red-800 hover:to-red-900 text-white rounded-lg transition-all font-inter font-bold shadow-lg shadow-red-800/20 disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isDeleting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Deleting...</span>
                      </>
                    ) : (
                      `Delete group (${groupFiles.length} files)`
                    )}
                  </button>
                )}
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="px-3 py-2 text-sm bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-lg transition-all font-inter font-bold shadow-lg shadow-red-500/20 disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Deleting...</span>
                    </>
                  ) : groupFiles.length > 1 ? (
                    'Delete this file only'
                  ) : (
                    'Delete'
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Link Files Modal */}
      {showLinkModal && (
        <LinkFilesModal
          file={file}
          onClose={() => setShowLinkModal(false)}
          onSuccess={() => onMove?.()}
        />
      )}

      {/* BeatStars & collaborator modal */}
      {showSellingModal &&
        createPortal(
          <EditSellingInfoModal
            file={file}
            onClose={() => setShowSellingModal(false)}
            onSuccess={() => onMove?.()}
          />,
          document.body
        )}

      {/* Edit Tags Modal */}
      {showEditTagsModal &&
        createPortal(
          <QuickEditModal
            file={file}
            onClose={() => setShowEditTagsModal(false)}
            onSave={() => {
              onMove?.();
              setShowEditTagsModal(false);
            }}
          />,
          document.body
        )}

      {showAddToBeatPackModal && (
        <AddToBeatPackModal
          fileIds={[file.id]}
          onClose={() => {
            setShowAddToBeatPackModal(false);
            onMove?.();
          }}
        />
      )}
    </>
  );
};
